import axios from "axios";
import CryptoJS from "crypto-js";
import { API_URL } from "../utilities/url";
import storage from "./storage";

const client = axios.create({
  baseURL: API_URL + "developer/",
  headers: { "Content-Type": "application/json" },
});

client.interceptors.request.use((config) => {
  const token = storage.get("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const encWS = storage.get("workspace");
  if (encWS) {
    try {
      const ws = JSON.parse(
        CryptoJS.AES.decrypt(encWS, "workspace").toString(CryptoJS.enc.Utf8)
      );
      if (ws && ws.idt_user_workspace) {
        config.headers["X-Workspace-ID"] = String(ws.idt_user_workspace);
      }
    } catch (_) {
      /* fall through; proxy will reject with 403 */
    }
  }
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (err) => {
    const data = err.response && err.response.data;
    const status = err.response && err.response.status;
    if (data && data.error) {
      const e = new Error(data.error.message || "Request failed");
      e.code = data.error.code;
      e.type = data.error.type;
      e.status = status;
      e.workspaceStale = status === 403;
      return Promise.reject(e);
    }
    return Promise.reject(err);
  }
);

export const listApps = (params) =>
  client.get("apps", { params }).then((r) => r.data);

export const getApp = (pubID) =>
  client.get(`apps/${encodeURIComponent(pubID)}`).then((r) => r.data);

export const createApp = (body) =>
  client.post("apps", body).then((r) => r.data);

export const deleteApp = (pubID) =>
  client.delete(`apps/${encodeURIComponent(pubID)}`).then((r) => r.data);

export const patchApp = (pubID, body) =>
  client.patch(`apps/${encodeURIComponent(pubID)}`, body).then((r) => r.data);

export const listCredentials = (pubID, params) =>
  client
    .get(`apps/${encodeURIComponent(pubID)}/credentials`, { params })
    .then((r) => r.data);

export const createCredential = (pubID, body) =>
  client
    .post(`apps/${encodeURIComponent(pubID)}/credentials`, body)
    .then((r) => r.data);

export const deleteCredential = (pubID, credID) =>
  client
    .delete(
      `apps/${encodeURIComponent(pubID)}/credentials/${encodeURIComponent(
        credID
      )}`
    )
    .then((r) => r.data);

// Webhook endpoints. The dashboard-admin path on otokata-core resolves the
// target app from `app_id` (body on POST, query on GET) — bearer callers would
// derive it from the credential, but we have no credential here. The proxy
// forwards as-is, only translating the path from kebab-case to snake-case on
// the wire.
export const listEndpoints = (appPubID, params) =>
  client
    .get("webhook-endpoints", {
      params: { ...(params || {}), app_id: appPubID },
    })
    .then((r) => r.data);

export const createEndpoint = (appPubID, body) =>
  client
    .post("webhook-endpoints", { ...body, app_id: appPubID })
    .then((r) => r.data);

export const getEndpoint = (endpointID) =>
  client
    .get(`webhook-endpoints/${encodeURIComponent(endpointID)}`)
    .then((r) => r.data);

export const patchEndpoint = (endpointID, body) =>
  client
    .patch(`webhook-endpoints/${encodeURIComponent(endpointID)}`, body)
    .then((r) => r.data);

export const deleteEndpoint = (endpointID) =>
  client
    .delete(`webhook-endpoints/${encodeURIComponent(endpointID)}`)
    .then((r) => r.data);

export const listDeliveries = (endpointID, params) =>
  client
    .get(`webhook-endpoints/${encodeURIComponent(endpointID)}/deliveries`, {
      params,
    })
    .then((r) => r.data);

export const redeliver = (endpointID, deliveryID) =>
  client
    .post(
      `webhook-endpoints/${encodeURIComponent(
        endpointID
      )}/deliveries/${encodeURIComponent(deliveryID)}/redeliver`
    )
    .then((r) => r.data);

// Events. Workspace-scoped on the backend — no app_id needed. listEvents
// accepts {event_type, livemode, limit, starting_after}; the proxy passes
// them straight through to otokata-core /v1/events.
export const listEvents = (params) =>
  client.get("events", { params }).then((r) => r.data);

export const getEvent = (eventID) =>
  client.get(`events/${encodeURIComponent(eventID)}`).then((r) => r.data);

export default client;
